import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, DollarSign, Users, Truck, Package, Building2,
  ArrowUpRight, ArrowDownRight, RefreshCw, Target, Award,
  Mail, FileText, MapPin, BarChart3, PieChart
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [brokerStats, setBrokerStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [contractStats, setContractStats] = useState(null);
  const [topDrivers, setTopDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAllStats = async () => {
    setLoading(true);
    try {
      const [statsRes, brokersRes, invoicesRes, emailsRes, contractsRes, driversRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/brokers/stats`),
        axios.get(`${API}/invoices/stats`),
        axios.get(`${API}/email/stats`),
        axios.get(`${API}/contracts/stats`),
        axios.get(`${API}/drivers?status=available&limit=10`)
      ]);
      
      setStats(statsRes.data);
      setBrokerStats(brokersRes.data);
      setInvoiceStats(invoicesRes.data);
      setEmailStats(emailsRes.data);
      setContractStats(contractsRes.data);
      setTopDrivers(driversRes.data || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  const StatCard = ({ icon: Icon, title, value, subtitle, color, trend }) => (
    <Card className="glass-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-black font-heading text-foreground">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
              <Icon className="w-6 h-6" style={{ color }} />
            </div>
            {trend && (
              <div className={`flex items-center gap-1 text-xs font-semibold ${trend > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(trend)}%
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ProgressBar = ({ value, max, color }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6 fade-in" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-black font-heading tracking-tight text-foreground">Analytics</h1>
            <p className="text-muted-foreground mt-2">Business intelligence & performance metrics</p>
          </div>
          <Button onClick={fetchAllStats} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            icon={Truck} 
            title="Total Fleet" 
            value={stats?.drivers?.total?.toLocaleString() || '0'}
            subtitle={`${stats?.drivers?.available?.toLocaleString() || 0} available`}
            color="#3B82F6"
          />
          <StatCard 
            icon={Building2} 
            title="Total Brokers" 
            value={brokerStats?.total?.toLocaleString() || '0'}
            subtitle={`${brokerStats?.contacted?.toLocaleString() || 0} contacted`}
            color="#8B5CF6"
          />
          <StatCard 
            icon={DollarSign} 
            title="Revenue Collected" 
            value={`$${(invoiceStats?.total_collected || 0).toLocaleString()}`}
            subtitle={`$${(invoiceStats?.total_pending || 0).toLocaleString()} pending`}
            color="#10B981"
          />
          <StatCard 
            icon={Package} 
            title="Total Loads" 
            value={stats?.loads?.total?.toLocaleString() || '0'}
            subtitle={`${stats?.loads?.completed || 0} completed`}
            color="#F59E0B"
          />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Broker Pipeline */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-400" />
                Broker Pipeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {brokerStats && (
                <>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-400">New (Untouched)</span>
                        <span className="text-sm font-bold text-blue-400">{brokerStats.new?.toLocaleString()}</span>
                      </div>
                      <ProgressBar value={brokerStats.new} max={brokerStats.total} color="#3B82F6" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-400">Contacted</span>
                        <span className="text-sm font-bold text-amber-400">{brokerStats.contacted?.toLocaleString()}</span>
                      </div>
                      <ProgressBar value={brokerStats.contacted} max={brokerStats.total} color="#F59E0B" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-400">Active Partners</span>
                        <span className="text-sm font-bold text-emerald-400">{brokerStats.active?.toLocaleString()}</span>
                      </div>
                      <ProgressBar value={brokerStats.active} max={brokerStats.total} color="#10B981" />
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Conversion Rate</span>
                      <span className="font-bold text-foreground">
                        {brokerStats.total > 0 
                          ? ((brokerStats.active / brokerStats.total) * 100).toFixed(2)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Outreach Performance */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                Outreach Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                  <p className="text-3xl font-black text-blue-400">{emailStats?.total_campaigns || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Campaigns</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                  <p className="text-3xl font-black text-emerald-400">{emailStats?.total_emails_sent?.toLocaleString() || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Emails Sent</p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-400 font-semibold">Brokers Reached</p>
                    <p className="text-2xl font-black text-white">{emailStats?.brokers_contacted?.toLocaleString() || 0}</p>
                  </div>
                  <Target className="w-10 h-10 text-emerald-500/50" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contract Status */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Contract Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contractStats && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                    <p className="text-2xl font-black text-slate-400">{contractStats.draft}</p>
                    <p className="text-xs text-slate-500 mt-1">Draft</p>
                  </div>
                  <div className="p-4 rounded-lg bg-amber-500/10 text-center">
                    <p className="text-2xl font-black text-amber-400">{contractStats.sent}</p>
                    <p className="text-xs text-slate-500 mt-1">Sent</p>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-500/10 text-center">
                    <p className="text-2xl font-black text-emerald-400">{contractStats.signed}</p>
                    <p className="text-xs text-slate-500 mt-1">Signed</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Revenue Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoiceStats && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-emerald-500/10">
                    <div>
                      <p className="text-xs text-slate-400">Collected</p>
                      <p className="text-2xl font-black text-emerald-400">${invoiceStats.total_collected?.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Invoices Paid</p>
                      <p className="text-xl font-bold text-white">{invoiceStats.paid}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-lg bg-amber-500/10">
                    <div>
                      <p className="text-xs text-slate-400">Pending</p>
                      <p className="text-2xl font-black text-amber-400">${invoiceStats.total_pending?.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Invoices Pending</p>
                      <p className="text-xl font-bold text-white">{invoiceStats.pending}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Fleet Coverage */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              Fleet Coverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-3xl font-black text-blue-400">{stats?.drivers?.total?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-400 mt-1">Total Drivers</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-3xl font-black text-emerald-400">{stats?.trucks?.total?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-400 mt-1">Total Trucks</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-3xl font-black text-purple-400">{brokerStats?.total?.toLocaleString() || 0}</p>
                <p className="text-sm text-slate-400 mt-1">Broker Network</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50 text-center">
                <p className="text-3xl font-black text-amber-400">50</p>
                <p className="text-sm text-slate-400 mt-1">States Covered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AnalyticsDashboard;
